import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";
import useCollectionCount from "astrid-firestore/src/hooks/useCollectionCount";

import { useKnownUser } from "../../../../authentication/state/knownUser";
import { useProfile } from "../../../../authentication/state/profile";
import featureFlags from "../../../../authorization/constants/featureFlags";
import useFeatureFlag from "../../../../authorization/hooks/useFeatureFlag";
import useHasRight from "../../../../authorization/hooks/useHasRight";
import useOrdersQuery from "../../../../orders/hooks/useOrdersQuery";

export default function useMainMenuOptions({ organization = {} }) {
	const { t } = useTranslation();

	const user = useProfile();
	const knownUser = useKnownUser();

	const { id, type } = organization;

	const hasRight = useHasRight();
	const location = useLocation();
	const hasFlatteningFeature = useFeatureFlag(featureFlags.FLATTENING, organization);

	const { pathname } = location;

	const ordersQuery = useOrdersQuery({ sent: false, expense: true, processed: false, secondPartyId: id });
	const [ordersAmount] = useCollectionCount(type === organizationTypes.NARRATOR && ordersQuery);

	if (pathname.startsWith("/account")) {
		return [
			{
				to: `/account/${user?.id}/settings`,
				text: t("settings", "Settings"),
				active: pathname.includes("settings"),
			},
			{
				to: `/account/${user?.id}/agreements`,
				text: t("agreements", "Agreements"),
				active: pathname.includes("agreements"),
				hidden: !knownUser,
			},
			{
				to: `/account/${user?.id}/revenues`,
				text: t("revenues", "Revenues"),
				active: pathname.includes("revenues"),
				hidden: type === organizationTypes.NARRATOR || !knownUser,
			},
			{
				to: `/account/${user?.id}/orders`,
				text: t("invoiceData", "Invoicing"),
				active: pathname.includes("orders"),
				hidden: type === organizationTypes.NARRATOR || !knownUser,
			},
		];
	}

	if (type === organizationTypes.ADMIN) {
		return [
			{
				to: `/${id}`,
				text: t("overview", "Overview"),
				active: pathname.endsWith(`/${id}`),
			},
			{
				to: "/admin/approve/costs",
				text: t("needsApproval", "Needs approval"),
				active: pathname.startsWith("/admin/approve"),
			},
			{
				to: "/admin/producers",
				text: t("producers", "Producers"),
				active: pathname.startsWith("/admin/producers"),
			},
			{
				to: "/admin/publishers",
				text: t("publishers", "Publishers"),
				active: pathname.startsWith("/admin/publishers"),
			},
			{
				to: "/admin/finance/cloud-revenues",
				text: t("finance", "Finance"),
				active: pathname.startsWith("/admin/finance"),
			},
			{
				to: `/${id}/works`,
				text: t("works", "Works"),
				active: pathname.includes("/works"),
				hidden: !hasFlatteningFeature,
			},
			{
				to: "/admin/articles",
				text: t("articles", "Articles"),
				active: pathname.startsWith("/admin/articles"),
				hidden: !hasFlatteningFeature,
			},
			{
				to: "/admin/productions",
				text: t("productions", "Productions"),
				active: pathname.startsWith("/admin/productions") || pathname.startsWith("/production/"),
			},
			{
				to: `/admin/distributions`,
				text: t("distributions", "Distributions"),
				active: pathname.startsWith("/admin/distributions"),
				hidden: !hasFlatteningFeature,
			},
			{
				to: "/admin/users",
				text: t("users", "Users"),
				active: pathname.startsWith("/admin/users"),
			},
			{
				to: "/admin/imports",
				text: t("imports", "Imports"),
				active: pathname.startsWith("/admin/imports"),
			},
			{
				to: "/admin/settings/channels",
				text: t("settings", "Settings"),
				active: pathname.startsWith("/admin/settings"),
			},
		];
	}

	if (type === organizationTypes.PRODUCER) {
		return [
			{
				to: `/${type}/${id}`,
				text: t("overview", "Overview"),
				active: pathname.endsWith(`/${type}/${id}`),
			},
			{
				to: `/${type}/${id}/create-production`,
				text: t("createProduction", "Create production"),
				active: pathname.startsWith(`/${type}/${id}/create-production`),
				hidden: !hasRight("productions.manage"),
			},
			{
				to: `/${type}/${id}/my-productions/current`,
				text: t("myProductions", "My productions"),
				active: pathname.startsWith(`/${type}/${id}/my-productions`),
			},
			{
				to: `/${type}/${id}/productions`,
				text: t("productions", "Productions"),
				active: pathname.startsWith(`/${type}/${id}/productions`) || pathname.startsWith("/production/"),
				hidden: !hasRight("productions.manage"),
			},
			{
				to: `/${type}/${id}/calendar`,
				text: t("calendar", "Calendar"),
				active: pathname.startsWith(`/${type}/${id}/calendar`),
				hidden: !hasRight("calendar.view"),
			},
			{
				to: `/${type}/${id}/documents`,
				text: t("documents", "Documents"),
				active: pathname.startsWith(`/${type}/${id}/documents`),
			},
			{
				to: `/${type}/${id}/statistics`,
				text: t("statistics"),
				active: pathname.startsWith(`/${type}/${id}/statistics`),
				hidden: !hasRight("productions.viewStatistics"),
			},
			{
				to: `/${type}/${id}/studios`,
				text: t("studios", "Studios"),
				active: pathname.startsWith(`/${type}/${id}/studios`),
				hidden: !hasRight("studios.view"),
			},
			{
				to: `/${type}/${id}/users`,
				text: t("users", "Users"),
				active: pathname.startsWith(`/${type}/${id}/users`),
				hidden: !hasRight("users.view"),
			},
			{
				to: `/${type}/${id}/publishers`,
				text: t("publishers", "Publishers"),
				hidden: !hasRight("publishers.view"),
				active: pathname.startsWith(`/${type}/${id}/publishers`),
			},
			{
				to: `/${type}/${id}/finance/expenses`,
				text: t("finance", "Finance"),
				hidden: !hasRight("admin"),
				active: pathname.startsWith(`/${type}/${id}/finance`),
			},
			{
				to: `/${type}/${id}/settings/vendors`,
				text: t("settings", "Settings"),
				hidden: !hasRight("settings.view"),
				active: pathname.startsWith(`/${type}/${id}/settings`),
			},
		];
	}

	if (type === organizationTypes.PUBLISHER) {
		return [
			{
				to: `/${type}/${id}`,
				text: t("overview", "Overview"),
				active: pathname.endsWith(`/${type}/${id}`),
			},
			{
				to: `/${type}/${id}/create-production`,
				text: t("createProduction", "Create production"),
				active: pathname.includes("create-production"),
			},
			{
				to: `/${type}/${id}/my-productions/current`,
				text: t("myProductions", "My productions"),
				active: pathname.includes("my-productions"),
			},
			{
				to: `/${type}/${id}/productions`,
				text: t("productions", "Productions"),
				active: pathname.startsWith(`/${type}/${id}/productions`) || pathname.startsWith("/production/"),
			},
			{
				to: "/reader",
				text: t("narrators", "Narrators"),
				active: pathname.startsWith(`/reader`),
				hidden: !hasRight("readers.view"),
			},
			{
				to: `/${type}/${id}/works`,
				text: t("works", "Works"),
				active: pathname.includes(`/${type}/${id}/works`),
				hidden: !hasFlatteningFeature,
			},
			{
				to: `/${type}/${id}/articles`,
				text: t("articles", "Articles"),
				active: pathname.includes(`/${type}/${id}/articles`),
				hidden: !hasFlatteningFeature,
			},
			{
				to: `/${type}/${id}/series`,
				text: t("series", "Series"),
				active: pathname.startsWith(`/${type}/${id}/series`),
				hidden: !hasFlatteningFeature,
			},
			{
				to: `/${type}/${id}/users`,
				text: t("users", "Users"),
				active: pathname.startsWith(`/${type}/${id}/users`),
			},
			{
				to: `/${type}/${id}/finance/sales-orders`,
				text: t("finance", "Finance"),
				hidden: !hasRight("orders.view"),
				active: pathname.startsWith(`/${type}/${id}/finance`),
			},
			{
				to: `/${type}/${id}/settings/vendors`,
				text: t("settings", "Settings"),
				hidden: !hasRight("admin"),
				active: pathname.startsWith(`/${type}/${id}/settings`),
			},
		];
	}

	if (type === organizationTypes.NARRATOR) {
		return [
			{
				to: `/${type}/${id}`,
				text: t("overview", "Overview"),
				active: pathname.endsWith(`/${type}/${id}`),
			},
			{
				to: `/${type}/${id}/my-productions/current`,
				text: t("myProductions", "My productions"),
				active: pathname.includes("my-productions"),
			},
			{
				to: `/${type}/${id}/calendar`,
				text: t("calendar", "Calendar"),
				active: pathname.startsWith(`/${type}/${id}/calendar`),
			},
			{
				to: `/${type}/${id}/orders`,
				text: t("invoiceData", "Invoicing"),
				label: ordersAmount || undefined,
				active: pathname.startsWith(`/${type}/${id}/orders`),
			},
		];
	}

	return [];
}
